import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, Link } from 'react-router-dom';

import { MiniTable } from '@rr/ui';
import { GET_STUDENT_LESSONS_QUERY } from './GetStudentLessons.query.gql';
import { GetStudentLessonsQuery, Lessons } from 'types/graphql';
import { humanDate } from '@rr/utils';

interface NameCell {
  value: string;
  cell: {
    row: {
      original: Lessons;
    };
  };
}

export const StudentLessons = () => {
  const { studentId }: { studentId: string } = useParams();
  const [offset, setOffset] = useState(0);
  const LIMIT = 5;

  useEffect(() => {
    setOffset(0);
  }, [studentId]);

  const updateOffset = (number: number) => setOffset(number);

  const { loading, error, data } = useQuery<GetStudentLessonsQuery>(
    GET_STUDENT_LESSONS_QUERY,
    {
      variables: {
        id: studentId,
        offset,
        limit: LIMIT,
      },
    }
  );

  if (error) {
    console.error(error);
    return <div>Error</div>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  const LESSON_COLUMNS = [
    {
      Header: 'Date',
      accessor: 'date',
    },
    {
      Header: 'Time',
      accessor: 'time',
    },
    {
      Header: 'Teacher',
      accessor: 'teacher',
      Cell: ({ value, cell }: NameCell) => (
        <Link to={`/teachers/${cell.row.original.teacher_id}`}>{value}</Link>
      ),
    },
    {
      Header: 'Instrument',
      accessor: 'instrument',
    },
    {
      Header: 'Location',
      accessor: 'location',
    },
  ];

  const lessonData =
    data?.students_by_pk?.lessons.map((lesson) => ({
      date: humanDate(lesson.start_time),
      start_time: humanDate(lesson.start_time, {
        minute: '2-digit',
        hour: '2-digit',
        hour12: true,
      }),
      end_time: humanDate(lesson.end_time, {
        minute: '2-digit',
        hour: '2-digit',
        hour12: true,
      }),
      teacher: lesson.user.gdpr_name,
      teacher_id: lesson.user.id,
      instrument: lesson.instrument?.prettyName,
      location: lesson.location?.name,
      plan: lesson.plan?.prettyName,
      plan_id: lesson.plan?.id,
    })) || [];

  return lessonData.length !== 0 ? (
    <MiniTable.Wrapper
      columns={LESSON_COLUMNS}
      data={lessonData}
      offset={offset}
      count={data?.students_by_pk?.lessons_aggregate.aggregate?.count}
      limit={LIMIT}
      updateOffset={updateOffset}
    >
      <MiniTable.Header />
      <MiniTable.Body />
      <MiniTable.Pagination />
    </MiniTable.Wrapper>
  ) : (
    <MiniTable.Zero>No lessons</MiniTable.Zero>
  );
};
